import _ from 'lodash';
import Moment from 'moment-timezone';
import delay from 'yoctodelay';
import { ElNotification } from 'element-plus';
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';

import { familyWidgetList, operatorWidgetList, partnerWidgetList, affiliateWidgetList } from '../sidebarLinks';
import type { CardType, ExtendedCardType } from '@/models/payment';
import type { SidebarItemType } from '@/models/app';
import type { CallerLovedOneIdName } from '@/models/callerLovedOne';
import type { OptionOrder, ParsedOption } from '@/models/deliveryOrder';
import type { GeoLocationType, LocationDataType, CustomLocationType, Address } from '@/models/address';
import { dateFormat, timeOnlyFormat } from '@/constants/app';
import { orderStatuses } from '@/constants/deliveryOrder';
import { cardImageList } from '@/constants/payment';
import { parseStripeDeclineMessage, getStripeErrorMessage, type StripeDeclinedCode } from '@/services/payments/stripeErrors';
import { isSimpleRideOrderingTest } from '@/use/customer';
import { useGlobal } from '@/use/global';
import { CallDirection } from '@/gql/graphql';
import { log } from './logger';

const { gogoNumbers } = useGlobal();

// API endpoint to get geocode of car of the ride
// We save the geocode of ride in another server to track the ride
const historyAPI = import.meta.env.VITE_RIDE_HISTORY_API_URL;
const isDevMode = import.meta.env.DEV;
const isProdMode = import.meta.env.PROD;
const gogoStripeKey = import.meta.env.VITE_STRIPE_GOGO_KEY || import.meta.env.VITE_STRIPE_KEY;
const arriveStripeKey = import.meta.env.VITE_STRIPE_ARRIVE_KEY;
const stripeKey: Record<string, string> = {
  '': gogoStripeKey,
  undefined: gogoStripeKey,
  null: gogoStripeKey,
  gogo: gogoStripeKey,
  arrive: arriveStripeKey,
};

// Google api is stored in environment
const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;

const fingerPrintToken = import.meta.env.VITE_FINGERPRINT_TOKEN;

// API endpoint to check the ride available of the location
const locationCheckerAPI = import.meta.env.VITE_LOCATION_CHECKER;

const checkEnvironemtVariables = () => {
  const domain = window.location.hostname;
  // check domain
  if (!(import.meta.env.VITE_API_URL || '').includes(domain)) {
    log.error(
      {
        func: 'checkEnvironemtVariables',
      },
      `The app is connected to ${import.meta.env.VITE_API_URL}`,
    );
  }
  // check stripe key
  if (isProdMode) {
    const isEveryKeyLive = Object.entries(stripeKey).every(([, v]) => v?.startsWith('pk_live_'));
    if (!isEveryKeyLive) {
      log.error(
        {
          func: 'checkEnvironemtVariables',
        },
        'Stripe connection is not using the live key.',
      );
      alert('Stripe connection is not using the live key.');
    }
  }
};

// API endpoint of main APIs
const getApiURL = () => import.meta.env.VITE_API_URL;

// Move item in array
const moveItem = (array: any, fromIndex: number, toIndex: number) => {
  if (array.length <= fromIndex) return;
  if (array.length <= toIndex) return;
  array.splice(toIndex, 0, array.splice(fromIndex, 1)[0]);
};

// Validate the phone number
const isPhoneNumberValid = (phoneNumber?: string | null, required = true) => {
  if (!required) {
    return true;
  }

  if (!phoneNumber) {
    return false;
  }

  /*
    this is custom code for
    - bugged ride phone number - 199999999*
    - test phone number - 1555*******
  */
  if (phoneNumber.startsWith('+199999999') || phoneNumber.startsWith('+1555')) {
    return true;
  }

  // PII-masked placeholder (e.g. ***-***-1234) for an OverseasOperator viewing a restricted caller.
  // The backend resolves the real number server-side before booking/SMS — treat it as valid here so
  // masking doesn't block getting an estimate or ordering a ride.
  if (/^\*{3}-\*{3}-\d{4}$/.test(phoneNumber)) {
    return true;
  }

  return isValidPhoneNumber(phoneNumber || '');
};

// RFC 2822 compliant email address validation
const isValidEmailAddress = (value: string): boolean =>
  /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(
    value,
  );

const isValidCallerId = (callerId: string): boolean => callerId.length === 36;

const isConferenceFriendlyNameValid = (phoneNumber: string) => {
  return /^WT/.test(phoneNumber);
};

const isClientIdValid = (input: string) => {
  return /^client:[a-zA-Z0-9]+$/.test(input);
};

// Compare JSON => true if they are same, false if they are difference
const compareJSON = (oldValue: any, newValue: any) => {
  return _.isEqual(oldValue, newValue);
};

// convert address object into string
/*
  {
    streetAddress: '1435 Garfield Avenue',
    city: 'Pasadena',
    state: 'CA',
    zip: '91104',
    country: 'US'
  } => `1435 Garfield Avenue, Pasadena, CA 91104, US`
*/
const getFormattedAddress = (address: Address) =>
  `${address.streetAddress}${address.streetAddress2 ? `, ${address.streetAddress2}` : ''}, ${address.city}, ${address.state} ${
    address.zip || address.postalCode
  }${address.country ? `, ${address.country}` : ''}`;

/*
  get address object from string address
  1435 Garfield Avenue, Pasadena, CA 91104, US =>
  {
    streetAddress: '1435 Garfield Avenue',
    city: 'Pasadena',
    state: 'CA',
    zip: '91104',
    country: 'US'
  }
*/
const getAddressObjectFromString = (addy: string) => {
  const addyArr = addy.split(', ');
  let stateZip: string[] = [];
  if (addyArr[2]) {
    stateZip = addyArr[2].split(' ');
  } else {
    stateZip[0] = '';
    stateZip[1] = '';
  }

  const streetAddress = addyArr[0];
  const city = addyArr[1];
  const state = stateZip[0];
  const zip = stateZip[1];
  const country = addyArr[3] || 'US';

  return {
    streetAddress: streetAddress,
    city: city,
    state: state,
    zip: zip,
    country: country,
  } as LocationDataType;
};

// format date with defined format and timezone
const formatDateTime = (date: any, format: string = 'YYYY-MM-DD', timeZone: any = null) => {
  if (!date) {
    return;
  }

  if (timeZone) {
    const momentDate = Moment.utc(date).tz(timeZone);

    return momentDate.isValid() ? momentDate.format(format) : null;
  }

  // format date time with local timezone
  const momentDate = Moment(date);
  return momentDate.isValid() ? momentDate.format(format) : null;
};

const isAccessMenu = (menuItem: SidebarItemType, userRoles: string[]) => {
  let isPermission = false;
  if (menuItem.roles) {
    menuItem.roles.map((role: string) => {
      const permission = userRoles.find((r: string) => r === role);
      if (permission) isPermission = isPermission || true;
      else isPermission = isPermission || false;
      return role;
    });
  } else {
    isPermission = true;
  }
  return isPermission;
};

// generate the sidebar regarding with user role
const getSideMenuItems = (pageCategory: string) => {
  const userRoles = JSON.parse(localStorage.getItem('roles') || '[]');
  let items: SidebarItemType[] = [];

  if (pageCategory === 'family') {
    items = familyWidgetList.filter((menuItem: SidebarItemType) => {
      return isAccessMenu(menuItem, userRoles);
    });
  } else if (pageCategory === 'partner') {
    items = partnerWidgetList.filter((menuItem: SidebarItemType) => {
      return isAccessMenu(menuItem, userRoles);
    });
  } else if (pageCategory === 'operator') {
    items = operatorWidgetList.filter((menuItem: SidebarItemType) => {
      return isAccessMenu(menuItem, userRoles);
    });
    items = items.map((menuItem: SidebarItemType) => {
      const customMenuItem = Object.assign({}, menuItem);
      if (menuItem.children) {
        const childrenItems = menuItem.children.filter((childMenuItem: SidebarItemType) => {
          return isAccessMenu(childMenuItem, userRoles);
        });
        customMenuItem.children = childrenItems;
      }
      return customMenuItem;
    });
  } else if (pageCategory === 'affiliate') {
    items = affiliateWidgetList.filter((menuItem: SidebarItemType) => {
      return isAccessMenu(menuItem, userRoles);
    });
  }

  // AB Test: swap ride-ordering paths to book-ride for test group users
  if ((pageCategory === 'family' || pageCategory === 'partner') && isSimpleRideOrderingTest.value) {
    items = items.map((item: SidebarItemType) => ({
      ...item,
      path: item.path?.replace('/ride-ordering', '/book-ride'),
      name: item.path?.endsWith('/ride-ordering') ? 'Book a Ride' : item.name,
      subItemList: item.subItemList?.map((sub: any) => ({
        ...sub,
        path: sub.path?.replace('/ride-ordering', '/book-ride'),
      })),
    }));
  }

  return items;
};

const setSuccessMessage = (message: string) => {
  let successHTML: string;
  successHTML = '<h3 class="success">';
  successHTML += message;
  successHTML += '</h3>';
  return successHTML;
};

// parse error response of REST api and make error text to show on the pages
const getErrorResponse = (error: any, isHTMLType: boolean = true, isCustomer: boolean = false) => {
  let errorText = '';
  const status = error.response && ((error.response.data && error.response.data.status) || error.response.status);

  if (status) {
    if (status < 500) {
      if (error.response && error.response.data && error.response.data.message) {
        errorText = error.response.data.message;
      } else if (error.response && error.response.data) {
        errorText = error.response.data;
      }
    } else {
      errorText = `${status}: Internal Server Error`;
    }
  } else {
    errorText = error.message;
  }

  errorText = (!isCustomer && parseStripeDeclineMessage(error.response?.data?.stripeDeclinedMessage)) || errorText;
  return isHTMLType ? `<h3 class="error">${convertTextToHtml(errorText)}</h3>` : errorText;
};

// parse error response of Graphql query and mutation and make error text to show on the pages
const getGraphqlError = (error: any, isHTMLType: boolean = true) => {
  let errorMessage: string;

  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    // Extract the main error message
    errorMessage = error.graphQLErrors[0].message;

    // Check for internal server error and extract detailed error information if available
    const errorCode = error.graphQLErrors[0].extensions.code;
    if (errorCode === 'INTERNAL_SERVER_ERROR') {
      // Check if exception errors exist and retrieve the first error's message
      const exceptionError = error.graphQLErrors[0].extensions?.exception?.errors?.[0];
      errorMessage = exceptionError?.message ?? errorMessage;
    }
  }
  // If network error exists, extract its message
  else if (error.networkError?.result?.errors?.length > 0) {
    errorMessage = error.networkError.result.errors[0].message;
  }
  // If no GraphQL or network errors, fall back to general error message
  else {
    errorMessage = error.message;
  }

  // If there is stripe declined error message we have to show it instead of general error message
  const stripeDeclineMessage = getGraphqlQLStripeDeclinedMessage(error);
  if (stripeDeclineMessage) {
    return stripeDeclineMessage;
  }

  return isHTMLType ? `<h3 class="error">${convertTextToHtml(errorMessage)}</h3>` : errorMessage;
};

const getGraphqlQLStripeDeclinedMessage = (error: any): string => {
  if (!error.graphQLErrors || error.graphQLErrors.length === 0) {
    return '';
  }

  const firstError = error.graphQLErrors[0];
  if (firstError.extensions?.exception?.name !== 'ChargeError') {
    return '';
  }

  const errorCode = firstError.extensions?.exception?.code;
  const stripeDeclineMessage = getStripeErrorMessage(errorCode as StripeDeclinedCode);

  return parseStripeDeclineMessage(firstError.extensions.exception.stripeDeclinedMessage ?? stripeDeclineMessage);
};

const getGraphqlErrorCode = (error: any) => {
  if (!error.graphQLErrors || error.graphQLErrors?.length === 0) {
    return '';
  }

  return error.graphQLErrors[0].extensions?.exception?.code;
};

/*
  general list type
  [
    { id: **, value: ***, label: **, alias?: *** },
    { id: **, value: ***, label: **, alias?: *** },
    ....
  ]
*/

const getLabelByField = (array: any, value: string | number, field: string = 'key') => {
  let label = '';

  for (const item of array) {
    if (item[field] === value) {
      label = item.label;

      break;
    }

    if (item.children) {
      const childItem = item.children.find((childItem: any) => childItem[field] === value);

      if (childItem) {
        label = childItem.label;

        break;
      }
    }
  }

  return label;
};

const getAliasByField = (array: any, value: string | number, field: string = 'key') => {
  const item = array.find((item: any) => item[field] === value);
  return item ? item.alias : '';
};

const getSymbolByField = (array: any, value: string | number, field: string = 'key') => {
  const item = array.find((item: any) => item[field] === value);
  return item ? item.symbol : '';
};

const getFieldByLabel = (array: any, label: string, field: string = 'key') => {
  const item = array.find((item: any) => item.label === label);
  return item ? item[field] : '';
};

// get duration mins from two dates
const getDurationMins = (startDateTime: string, endDateTime: string = '') => {
  const startTime = startDateTime ? new Date(startDateTime) : new Date();
  const endTime = endDateTime ? new Date(endDateTime) : new Date();
  const diff = Math.abs(startTime.getTime() - endTime.getTime());
  const diffMins = Math.floor(diff / 1000 / 60);
  return diffMins;
};

// name capitalized
// shang tao => Shangtao
// jin => Jin
const getNameCapitalized = (name: string) => {
  if (!name) return '';
  let nameCapitalized = name.toLowerCase().replace(/ /g, '');
  nameCapitalized = nameCapitalized.charAt(0).toUpperCase() + nameCapitalized.slice(1);
  return nameCapitalized;
};

// prevent special character to avoid issue when we send the params in to query
const preventSpecialCharacter = (event: any) => {
  const regex = new RegExp("^[a-zA-Z0-9-._~:/\\[\\]?#@!$&'()*+,;=\r\n ]+$");
  const key = String.fromCharCode(!event.charCode ? event.which : event.charCode);
  if (!regex.test(key)) {
    event.preventDefault();
    return false;
  }
  return true;
};

// Convert 5:30 PM to YYYY-MM-DD 17:30 for time picker component
const convertTime = (strTime: string) => {
  const times = strTime.split(' ');
  const hrsMins = times[0].split(':');
  let time = '2000-01-01 ';
  if (times[1] && times[1].toUpperCase() === 'PM') {
    if (Number(hrsMins[0]) !== 12) {
      hrsMins[0] = Number(hrsMins[0]) + 12 + '';
    }
  } else if (Number(hrsMins[0]) === 12) {
    hrsMins[0] = '0';
  }
  time += Number(hrsMins[0]) > 9 ? hrsMins[0] : `0${Number(hrsMins[0])}`;
  time += `:${hrsMins[1]}`;
  return time;
};

// remove tab from string
const removeTabCharacterFromString = (text: string) => {
  const regReplace = new RegExp('\t', 'g');
  const removedText = text ? text.replace(regReplace, '') : '';
  return removedText;
};

// format multi line text with multi line html to show html format on pages
/*
  ex: 'This is test.\nPlease check it` to `This is test.<br />Please check it`
*/
const convertTextToHtml = (text?: string, isBoldStarSurroundedText: boolean = false) => {
  const regReplaceForBreakLine = new RegExp('\n', 'g');
  let htmlText = text ? text.replace(regReplaceForBreakLine, '<br />') : '';

  if (isBoldStarSurroundedText) {
    htmlText = htmlText.replace(/\*(.*?)\*/g, '<span class="text-highlight">$1</span>');
  }

  return htmlText;
};

// move scroll to the top
/*
  We have difference action for operator and family dashboard due to they have difference layout.
  In operator dashboard, the scroll is exisit in the content panel.
  In family dashboard, the scroll is exist in the whole page.
  Because in family dashboard, there is no right panel
*/
const moveToTop = () => {
  const operatorDashboardContent = document.querySelector('.main-panel.dynamic-panel .content');
  if (operatorDashboardContent) {
    operatorDashboardContent.scrollTop = 0;

    return;
  }

  const familyDashboardContent = document.querySelector('.main-panel .content');
  if (familyDashboardContent) {
    familyDashboardContent.scrollIntoView();
  }
};

// move scroll to defined component with hash
const moveScrollComponent = async (componentId: string) => {
  if (componentId) {
    const section = document.querySelector(componentId) as HTMLElement;
    if (section) {
      await delay(500);

      section.scrollIntoView();
    }
  } else {
    moveToTop();
  }
};

const moveToBottom = () => {
  const operatorDashboardContent = document.querySelector('.main-panel.dynamic-panel .content');
  if (operatorDashboardContent) {
    operatorDashboardContent.scrollTo(0, operatorDashboardContent.scrollHeight);

    return;
  }

  const familyDashboardContent = document.querySelector('.main-panel .content');
  if (familyDashboardContent) {
    familyDashboardContent.scrollTo(0, familyDashboardContent.scrollHeight);
  }
};

// merge first name and last name
// firstName: ShangTao, lastName: Jin, result: ShangTao Jin
const getFullName = (firstName?: string | null, lastName?: string | null, prefix?: string) => {
  let name = '';
  if (firstName && lastName) {
    name = `${firstName} ${lastName}`;
  } else if (firstName) {
    name = firstName;
  } else if (lastName) {
    name = lastName;
  }

  return prefix ? `${prefix} ${name}` : name;
};

const optionsFormatter = (options: OptionOrder[]): ParsedOption[] => {
  const result: ParsedOption[] = [];
  for (const option of options) {
    const item: ParsedOption = { label: '', optionLabel: option.title };

    for (const value of option.values) {
      if (option.selectedValues[value.id]) {
        if (value.subOptions && value.subOptions.length) {
          result.push(...optionsFormatter(value.subOptions));
        }

        if (!item.label.length) {
          item.label += ' ( ';
        } else {
          item.label += ', ';
        }

        item.label += `${value.title}${
          option.selectedValues[value.id] != true ? ' ( Quantity: ' + option.selectedValues[value.id] + ' )' : ''
        }`;

        if (value.price) {
          item.label += ` $ ${(value.price / 100).toFixed(2)}`;
        }
      }
    }

    if (item.label.length) item.label += ' )';

    if (item.label) {
      result.push(item);
    }
  }
  return result;
};

const hasUnsuccessfullyStatus = (order: any) => {
  return [orderStatuses.inactive.error, orderStatuses.inactive.canceled].includes(order.orderStatus.title);
};

const isResolvedWithReOrder = (order: any) => _.get(order, 'relatedPath.orderResolution.resolvedByOrderID');

const isResolvedWithoutReOrder = (order: any) => _.get(order, 'relatedPath.orderResolution.resolvedWithoutReorder');

const isResolvesOrder = (order: any) => _.get(order, 'relatedPath.orderResolution.resolvedOrderID');

const isReorderedWithReOrder = (order: any) => _.get(order, 'relatedPath.orderResolution.redeliveryByOrderId');

// check path is same, it will be used to prevent redirect to same path
const isDuplicatedPath = (oldPath: string, newPath: string) => oldPath === newPath;

// download json data into csv with defined file name
const downloadJSONToCSV = (jsonData: any, fileName: string) => {
  const file = `data:text/html;charset=utf-8,${jsonData}`;
  const data = encodeURI(file);
  const link = document.createElement('a');
  link.setAttribute('href', data);
  link.setAttribute('download', fileName);
  link.click();
};

// parse string and make headers array
const formatCSVHeader = (header: string) => {
  const headerArray = header.split(',');
  let headers = '';

  for (const [index, item] of headerArray.entries()) {
    if (index !== 0) {
      headers += ',';
    }
    headers += '"' + _.startCase(item) + '"';
  }
  return headers;
};

// compare two addresses to check what the address is changed or not
const isAddressChanged = (newValue: any, oldValue: any) => {
  if (!newValue || !oldValue) {
    return true;
  }

  const differences = _.omitBy(newValue, (v, k) => oldValue[k] === v);
  const keys = _.keys(differences);

  // nothing is changed, return false
  if (keys.length === 0) return false;

  // one thing is changed, it is a latitude or longitude, return false
  if (keys.length === 1 && (keys.includes('latitude') || keys.includes('longitude'))) return false;

  // two things are changed, but they are latitude and longitude, return false
  if (keys.length === 2 && keys.includes('latitude') && keys.includes('longitude')) return false;

  // address is changed
  return true;
};

// generate service string from object
const parseServiceAvailable = (data: any, streetAddress: string = '') => {
  const cityName = data.cityName;

  let countWord = 'many';
  if (data.userCount > 1000) {
    countWord = 'thousands';
  } else if (data.userCount > 100) {
    countWord = 'hundreds';
  }

  const surgeFactor = data.surgeFactor;
  const costPerMile = data.perMile / surgeFactor;

  const gogoFeePerMin = data.gogoFeePerMin;
  const costPerMin = data.perMinute / surgeFactor - gogoFeePerMin;

  const minimumFareHtml = data.minimumFare > data.baseFare ? `(with a minimum fare of $${data.minimumFare})` : '';

  let htmlString = '';
  htmlString += '<div class="mb-10" style="color: #ffa500; font-size: 24px;">Our service is available in your area!</div>';
  if (streetAddress) {
    htmlString += '<div class="service-content">';
    htmlString += `<p>In ${streetAddress}`;
    htmlString += '</div>';
  }
  htmlString += '<div class="service-content">';
  htmlString += `<p style="color: #008080; font-size: 16px;">Join ${countWord} users in ${cityName}!</p>`;
  htmlString += '</div>';
  htmlString += '<div class="service-content">';
  htmlString += '<h3 style="color: black; margin: 0px;"><strong>Ride Fare:&nbsp;&nbsp;&nbsp;</strong></h3>';
  htmlString +=
    '<p>$' +
    data.baseFare +
    ' base fare +&nbsp;$' +
    costPerMile.toFixed(2) +
    ' per mile +&nbsp;$' +
    costPerMin.toFixed(2) +
    ' per minute ' +
    minimumFareHtml +
    '</p>';
  htmlString += '</div>';

  htmlString += '<div class="service-content">';
  htmlString += '<h3 style="margin: 0px; color: black;"><strong>GoGoGrandparent Concierge Fee:&nbsp;&nbsp;&nbsp;</strong></h3>';
  htmlString += '<p>$' + gogoFeePerMin + ' per minute while you are in the vehicle</p>';
  htmlString += '</div>';

  htmlString += '<h3 class="error-message">*Please note that during periods of high demand, our vendors may include a surge charge</h3>';

  return htmlString;
};

const getPromotionByStoreId = (storeGroup: any, storeId: string) => {
  return _.get(_.find(_.get(storeGroup, 'stores', []), { id: storeId }), 'promotion', {});
};

const getPhoneNumber = (callLog: any) => {
  if (callLog == null) {
    return null;
  }

  let isOutbound: boolean;
  if (callLog.direction) {
    // primary way to detect direction from backend
    isOutbound = callLog.direction === CallDirection.Outbound;
  } else {
    // fallback way to detect direction from frontend config (not reliable)
    isOutbound = gogoNumbers.value.includes(callLog.fromPhoneNumber) || (callLog.fromPhoneNumber || '').includes('client');
  }

  return {
    customer: isOutbound ? callLog.toPhoneNumber : (callLog.resolvedPhoneNumber ?? callLog.fromPhoneNumber),
    gogo: isOutbound ? callLog.fromPhoneNumber : callLog.toPhoneNumber,
  };
};

// get cx phone number from callLog
const getCallingPhoneNumber = (callLog: any) => {
  return getPhoneNumber(callLog)?.customer;
};

// get card icon regarding with card type from defined card image list
const getCardIcon = (brandName: string) => {
  const selectCard = brandName && cardImageList.find((item: any) => item.type.toLowerCase() === brandName.toLowerCase());

  if (selectCard) {
    return selectCard.imgUrl;
  } else {
    return 'http://icons.iconarchive.com/icons/webiconset/application/128/Invoice-icon.png';
  }
};

const isCardExpired = (card: ExtendedCardType) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const expYear = parseInt(card.exp_year?.toString() ?? '');
  const expMonth = parseInt(card.exp_month?.toString() ?? '');

  if (currentYear < expYear) {
    return false;
  }
  if (currentYear > expYear) {
    return true;
  }

  // Same year
  return currentMonth > expMonth;
};

const formatCreditCardInfo = (cardItem: CardType) => {
  const card = {
    ...cardItem,
    last4: cardItem.last4 ? cardItem.last4 : 'XXXX',
    exp_month: Number(cardItem.exp_month) >= 10 ? cardItem.exp_month : `0${cardItem.exp_month}`,
    billingAddress: `${cardItem.address_line1}${cardItem.address_line2 ? ` ${cardItem.address_line2}` : ''}, ${cardItem.address_city}, ${
      cardItem.address_state
    } ${cardItem.address_zip}, ${cardItem.address_country}`,
  } as ExtendedCardType;

  card.expiration = `${card.exp_month}/${card.exp_year}`;
  card.is_expired = isCardExpired(card);

  if (!cardItem.metadata || !cardItem.metadata.addedBy || !cardItem.metadata.userName) {
    return card;
  }

  card.cardAdderInfo = `${cardItem.metadata.userName} (${cardItem.metadata.addedBy})`;

  if (card.metadata?.createdAt) {
    card.createdTime = formatDateTime(new Date(card.metadata?.createdAt * 1000), timeOnlyFormat);
    card.createdAt = formatDateTime(new Date(card.metadata?.createdAt * 1000), dateFormat);
  }

  return card;
};

const normalizePlanName = (planName: string) => {
  if (planName === 'basic10' || planName === 'basic15' || planName === 'basic17' || planName === 'basic15OpsFee5') {
    return 'basic';
  }

  if (planName === 'value25' || planName === 'value30') {
    return 'value';
  }

  if (['basicAnnualSave18', 'basicAnnualSave31', 'basicAnnualSave42'].includes(planName)) {
    return 'basicAnnual';
  }

  if (['valueAnnualSave18', 'valueAnnualSave31', 'valueAnnualSave42'].includes(planName)) {
    return 'valueAnnual';
  }

  if (['valueQuarterSave28', 'valueQuarterSave18'].includes(planName)) {
    return 'valueQuarter';
  }

  return planName;
};

const updateMarkerPosition = (marker: any, lat: number | string | null, lng: number | string | null, isAddressChanged: boolean = false) => {
  marker.isShow = false;

  if (lat && lng) {
    marker.position.lat = Number(lat);
    marker.position.lng = Number(lng);
    marker.isShow = true;

    if (isAddressChanged) {
      marker.initialPosition.lat = Number(lat);
      marker.initialPosition.lng = Number(lng);
    }
  } else {
    marker.position = { lat: 0.0, lng: 0.0 } as GeoLocationType;
    marker.initialPosition = { lat: 0.0, lng: 0.0 } as GeoLocationType;
    marker.isShow = false;
  }
};

const updateAddressWithGeocode = (address: CustomLocationType, lat: number | string | null, lng: number | string | null) => {
  address.latitude = lat ? Number(lat) : null;
  address.longitude = lng ? Number(lng) : null;

  return address;
};

const formatAmountWithCurrency = (amount: number) => {
  if (!amount) {
    return '$0';
  }

  if (amount > 0) {
    return `$${amount}`;
  }

  return `-$${Math.abs(amount)}`;
};

// if phone number is valid, return phone number again
// otherwise, return empty string
const getValidPhoneNumber = (phoneNumber?: string | null) => {
  return phoneNumber && isPhoneNumberValid(phoneNumber) ? phoneNumber : '';
};

const getOptionalNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const checkAndGetBelongsWarningMessage = (
  customer: any,
  callerLovedOneIdName: CallerLovedOneIdName | null,
  {
    isFamilyDashboard = false,
    partnerId = 0,
  }: {
    isFamilyDashboard?: boolean;
    partnerId?: number;
  } = {},
) => {
  // no need to show warning if the phone number does not belong to anyone else
  if (!callerLovedOneIdName) {
    return '';
  }

  // no need to show warning if it is the same customer
  if (callerLovedOneIdName.callerId === customer.id) {
    return '';
  }

  const hideBelongCustomerDetail = isFamilyDashboard || (partnerId && partnerId !== callerLovedOneIdName.partnerId);

  // if it gets here, the preferred contact phone number does not belong to the customer
  const customer1Name = getFullName(customer.firstName, customer.lastName);
  const customer2Name = getFullName(callerLovedOneIdName.callerFirstName ?? '', callerLovedOneIdName.callerLastName ?? '');

  let preferredContactWarning = hideBelongCustomerDetail
    ? `This ride is under ${customer1Name}'s account but the Preferred Contact Number belongs to someone else!`
    : `This ride is under ${customer1Name}'s account but the Preferred Contact Number belongs to ${customer2Name}!`;

  if (callerLovedOneIdName.lovedOneId) {
    const customer2LovedOneName = getFullName(callerLovedOneIdName.lovedOneFirstName ?? '', callerLovedOneIdName.lovedOneLastName ?? '');

    preferredContactWarning = hideBelongCustomerDetail
      ? preferredContactWarning
      : `This ride is under ${customer1Name}'s account but the Preferred Contact Number belongs to ${customer2LovedOneName}, the Loved One of ${customer2Name}!`;
  }

  return preferredContactWarning;
};

const getWebsiteUrl = () => {
  const hostName = window.location.hostname;
  const pathName = window.location.pathname;
  const protocol = window.location.protocol;

  return `${protocol}//${hostName}${pathName}`;
};

const generateRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';

  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }

  return color;
};

const formatPastWeekText = (value: number) => {
  if (!value) {
    return 'Current Week';
  }

  if (value === 1) {
    return `1 week ago`;
  }

  return `${value} weeks ago`;
};

const convertBooleanToYesNo = (value?: boolean | null) => {
  return value ? 'Yes' : 'No';
};

const convertBooleanToYesOrBlank = (value?: boolean | null) => {
  return value ? 'Yes' : '';
};

const makePickupDropoffDriverNotes = (driverNotePickUp: string, driverNoteDropOff: string) => {
  let noteToDriver: string;
  if (driverNotePickUp && driverNoteDropOff) {
    noteToDriver = `${driverNotePickUp}\n${driverNoteDropOff}`;
  } else if (driverNotePickUp) {
    noteToDriver = driverNotePickUp;
  } else if (driverNoteDropOff) {
    noteToDriver = driverNoteDropOff;
  } else {
    noteToDriver = '';
  }

  return noteToDriver;
};

const copyText = (content: string, fieldName: string = '') => {
  if (!content) {
    return;
  }

  navigator.clipboard.writeText(content);

  ElNotification({
    message: `${fieldName || content} is copied.`,
    type: 'success',
    title: 'Success!',
  });
};

const convertLineBreakTextIntoBulletPointsHtml = (message: string) => {
  if (!message) {
    return '';
  }

  const texts = message.split('\n');

  let htmlText = '<ul>';

  for (const text of texts) {
    if (!text) {
      continue;
    }

    htmlText += `<li style="text-align: left;">${text}</li>`;
  }

  htmlText += '</ul>';

  return htmlText;
};

export const clickSectionEditButton = (sectionName: string) => {
  const sectionEditText = document.querySelector(`.right-panel-content section#${sectionName} .edit-link a.edit-text`) as HTMLElement;
  if (sectionEditText) {
    sectionEditText.click();
  }
};

export const scrollToElement = (element: string) => {
  const scrollElement = document.querySelector(`${element}`) as HTMLElement;
  scrollElement.scrollIntoView({ behavior: 'smooth' });
};

export function removeEmojis<T extends string | number | undefined | null>(text: T): T {
  /*
    The regular expression [^\x00-\x7F] targets non-ASCII characters
    (emojis usually have code points outside of the basic ASCII range).
  */
  if (typeof text === 'string') {
    return text.replace(/[^\x00-\x7F]/g, '') as T;
  }
  return text;
}

// convert html to text.
const htmlToCleanText = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  // Replace <br> with newline
  div.querySelectorAll('br').forEach((br: HTMLBRElement) => {
    const newline = document.createTextNode('\n');
    br.parentNode?.replaceChild(newline, br);
  });

  return div.textContent || '';
};

const formatSecondsDuration = (seconds: number) => {
  const duration = Moment.duration(seconds, 'seconds');

  const hours = duration.hours();
  const minutes = duration.minutes();
  const secs = duration.seconds();

  const formattedDuration = (hours ? `${hours}h ` : '') + (minutes ? `${minutes}m ` : '') + (secs ? `${secs}s` : '');

  return formattedDuration;
};

const pickDefined = <T extends object, K extends keyof T>(obj: T, keys: K[]): Partial<T> =>
  keys.reduce((result, key) => {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
    return result;
  }, {} as Partial<T>);

const validateFullUrl = (url: string) => {
  try {
    const parsed = new URL(url);

    // Must be http or https
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }

    // Must have domain
    if (!parsed.hostname) {
      return false;
    }

    // Must have pathname and not just "/"
    if (!parsed.pathname || parsed.pathname === '/') {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const currencySymbols: Record<string, string> = {
  AUD: 'A$',
  CAD: 'CA$',
  USD: '$',
};

const formatNullablePrice = (price: number | null | undefined, currency?: string) => {
  if (price == null) {
    return '-';
  }

  const currencyCode = (currency || 'USD').toUpperCase();
  const symbol = currencySymbols[currencyCode] ?? `${currencyCode} `;

  if (price % 1) {
    return `${symbol}${price.toFixed(2)}`;
  } else {
    return `${symbol}${price.toFixed(0)}`;
  }
};

const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

const capitalizeWords = (text: string): string => text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    return '';
  }

  try {
    return parsePhoneNumberWithError(phoneNumber).formatNational();
  } catch {
    return phoneNumber;
  }
};

export default {
  isDevMode,
  isProdMode,
  stripeKey,
  historyAPI,
  getApiURL,
  locationCheckerAPI,
  moveItem,
  isPhoneNumberValid,
  isValidEmailAddress,
  isConferenceFriendlyNameValid,
  isClientIdValid,
  compareJSON,
  googleApiKey,
  getAddressObjectFromString,
  formatDateTime,
  getSideMenuItems,
  getErrorResponse,
  getGraphqlError,
  getGraphqlErrorCode,
  getGraphqlQLStripeDeclinedMessage,
  setSuccessMessage,
  checkEnvironemtVariables,
  getDurationMins,
  getNameCapitalized,
  preventSpecialCharacter,
  convertTime,
  convertTextToHtml,
  removeTabCharacterFromString,
  moveScrollComponent,
  getFullName,
  moveToTop,
  optionsFormatter,
  hasUnsuccessfullyStatus,
  isResolvedWithReOrder,
  isResolvedWithoutReOrder,
  isResolvesOrder,
  isReorderedWithReOrder,
  isDuplicatedPath,
  downloadJSONToCSV,
  formatCSVHeader,
  isAddressChanged,
  parseServiceAvailable,
  getFormattedAddress,
  getPromotionByStoreId,
  getPhoneNumber,
  getCallingPhoneNumber,
  getCardIcon,
  getLabelByField,
  getAliasByField,
  getSymbolByField,
  getFieldByLabel,
  formatCreditCardInfo,
  normalizePlanName,
  updateMarkerPosition,
  formatAmountWithCurrency,
  getValidPhoneNumber,
  getOptionalNumber,
  fingerPrintToken,
  checkAndGetBelongsWarningMessage,
  getWebsiteUrl,
  generateRandomColor,
  formatPastWeekText,
  convertBooleanToYesNo,
  convertBooleanToYesOrBlank,
  updateAddressWithGeocode,
  makePickupDropoffDriverNotes,
  isValidCallerId,
  copyText,
  moveToBottom,
  convertLineBreakTextIntoBulletPointsHtml,
  clickSectionEditButton,
  scrollToElement,
  htmlToCleanText,
  formatSecondsDuration,
  pickDefined,
  validateFullUrl,
  formatNullablePrice,
  isValidUrl,
  capitalizeWords,
  formatPhoneNumber,
};
